import {
	Transform,
	TransformRotation,
	TransformPosition,
	BoxDimensions,
} from '@ikea-aoa/elements-3d';
import { TransformVector } from '../utils/TransformVector';

export interface GridBoxBoundings {
	top: number;
	bottom: number;
	right: number;
	left: number;
}

export class GridBox {
	private _position: TransformPosition;
	private _rotation: TransformRotation;
	private _size: BoxDimensions;
	private _boundsPosition: TransformPosition;
	private _threshold: number | { width: number; depth: number };
	private _box: GridBoxBoundings;

	constructor(
		size: BoxDimensions,
		position: TransformPosition = TransformVector.zero(),
		rotation: TransformRotation = TransformVector.zero(),
		boundsPosition: TransformPosition = TransformVector.zero(),
		threshold: number | { width: number; depth: number } = 0
	) {
		this._position = position;
		this._rotation = rotation;
		this._size = size;
		this._threshold = threshold;
		this._boundsPosition = boundsPosition;
		this._box = {
			top: 0,
			bottom: 0,
			right: 0,
			left: 0,
		};
		this.calculateBoundingBox();
	}

	// get box
	public getBox(): GridBoxBoundings {
		return this._box;
	}

	// check intersection
	public intersects(gridBox: GridBox): boolean {
		const a = this.getBox();
		const b = gridBox.getBox();
		return !(b.left > a.right || b.right < a.left || b.top < a.bottom || b.bottom > a.top);
	}

	// clone grid box
	public clone(): GridBox {
		return new GridBox(
			this._size,
			this._position,
			this._rotation,
			this._boundsPosition,
			this._threshold
		);
	}

	// position
	public get position(): TransformPosition {
		return this._position;
	}
	public set position(value: TransformPosition) {
		this._position = value;
		this.calculateBoundingBox();
	}

	// rotation
	public get rotation(): TransformRotation {
		return this._rotation;
	}
	public set rotation(value: TransformRotation) {
		this._rotation = value;
		this.calculateBoundingBox();
	}

	// size
	public get size(): BoxDimensions {
		return this._size;
	}
	public set size(value: BoxDimensions) {
		this._size = value;
		this.calculateBoundingBox();
	}

	// bounds position
	public get boundsPosition(): TransformPosition {
		return this._boundsPosition;
	}
	public set boundsPosition(value: TransformPosition) {
		this._boundsPosition = value;
		this.calculateBoundingBox();
	}

	// threshold
	public get threshold(): number | { width: number; depth: number } {
		return this._threshold;
	}
	public set threshold(value: number | { width: number; depth: number }) {
		this._threshold = value;
		this.calculateBoundingBox();
	}

	// set transform
	public setTransform(transform: Transform): void {
		this._position = transform.position as TransformPosition;
		this._rotation = transform.rotation as TransformRotation;
		this.calculateBoundingBox();
	}

	public calculateBoundingBox(): void {
		let boxDimensions = this.applyThreshold(this._size, this._threshold);
		boxDimensions = this.rotateBoxDimensions(boxDimensions, this._rotation);
		const boundsPosition = TransformVector.rotate(this._boundsPosition, this._rotation);

		this._box.right =
			(this._position.x as number) + (boundsPosition.x as number) + boxDimensions.width / 2;
		this._box.left =
			(this._position.x as number) + (boundsPosition.x as number) - boxDimensions.width / 2;

		this._box.top =
			(this._position.z as number) + (boundsPosition.z as number) + boxDimensions.depth / 2;
		this._box.bottom =
			(this._position.z as number) + (boundsPosition.z as number) - boxDimensions.depth / 2;
	}

	private applyThreshold(
		size: BoxDimensions,
		threshold?: number | { width: number; depth: number }
	): BoxDimensions {
		const clonedSize: BoxDimensions = {
			width: size.width,
			height: size.height,
			depth: size.depth,
		};
		if (threshold) {
			if (typeof threshold == 'number') {
				clonedSize.width -= threshold;
				clonedSize.depth -= threshold;
			} else {
				clonedSize.width -= threshold.width;
				clonedSize.depth -= threshold.depth;
			}
		}
		return clonedSize;
	}

	private rotateBoxDimensions(size: BoxDimensions, rotation: TransformRotation): BoxDimensions {
		if (rotation.x == 0 && rotation.y == 0 && rotation.z == 0) {
			return size;
		}
		let vector = {
			x: size.width,
			y: size.height,
			z: size.depth,
		} as TransformPosition;
		vector = TransformVector.rotate(vector, rotation);

		return {
			width: Math.abs(vector.x as number),
			height: Math.abs(vector.y as number),
			depth: Math.abs(vector.z as number),
		} as BoxDimensions;
	}
}
